import React, { useEffect, useMemo, useState } from 'react';
import { ThumbsUp, ThumbsDown, AlertCircle, Hash, ChevronRight, ChevronDown, Clock } from 'lucide-react';

interface Props {
  item: any;              // DisputeItemLimit (do itemLimits da sessão)
  itensGrupoRow?: any;    // Row correspondente de rankingData.itensGrupo['-1']
  detalhamento?: any;     // rankingData.detalhamentoItem[N] — descricaoDetalhada, quantidadeSolicitada, etc
  lancesItem?: any[];     // rankingData.lancesPorParticipante[N].items
  propostasItem?: any[];  // rankingData.propostasIniciais[N].items
  meuValorUnitario?: number | null;  // rankingData.meuLanceUnitarioPorItem[N] — valor IPCOM por item
  /** PCP "Por Valor Global": bestBid/lastBid do item JÁ SÃO o total do lote
   *  (espelhados em todos os itens do groupNumber) — não é unitário. */
  valorGlobalLote?: boolean;
  sessionId: number;
  sessionStatus: string;
  index: number;
  onLimitChange: () => void;
  onOpenDrawer: (itemNumber: number, description: string) => void;
}

const fmtBRL4 = (v: any) => {
  const n = typeof v === 'number' ? v : (v ? parseFloat(String(v)) : NaN);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

const fmtNum4 = (v: any) => {
  const n = typeof v === 'number' ? v : (v ? parseFloat(String(v)) : NaN);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

const STATUS_PARTICIPANTE: Record<string, { label: string; color: string; icon: any }> = {
  G: { label: 'VENCENDO', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50', icon: ThumbsUp },
  P: { label: 'PARTICIPANDO', color: 'bg-blue-500/20 text-blue-300 border-blue-500/50', icon: Hash },
  E: { label: 'EMPATADO', color: 'bg-amber-500/20 text-amber-300 border-amber-500/50', icon: Hash },
  D: { label: 'DESCLASSIFICADO', color: 'bg-red-500/20 text-red-300 border-red-500/50', icon: AlertCircle },
};

/** Card vertical para 1 item da disputa, substituindo a linha-de-tabela tradicional.
 *  Combina dados editáveis do session.itemLimits (limite manual do user) com dados
 *  ao vivo do rankingData.itensGrupo['-1'] (melhor unit, meu unit, situação, etc). */
export function DisputeItemCard({ item, itensGrupoRow, detalhamento, lancesItem, propostasItem, meuValorUnitario, valorGlobalLote = false, onOpenDrawer }: Props) {
  // Dados autoritativos do detalhamento (endpoint /itens/{N}/detalhamento) — fonte rica
  const descricaoFull = detalhamento?.descricaoDetalhada || detalhamento?.descricao || item.description;
  const qtdReal = detalhamento?.quantidadeSolicitada ?? detalhamento?.quantidadeMinimaSolicitada ?? item.quantity;
  const unidadeReal = detalhamento?.unidadeFornecimento || item.unit;
  const valorEstimadoUnit = detalhamento?.valorEstimadoUnitario ?? detalhamento?.valorEstimado ?? item.proposalValue;
  const valorEstimadoTotal = detalhamento?.valorEstimadoTotal ?? (typeof qtdReal === 'number' && typeof valorEstimadoUnit === 'number' ? qtdReal * valorEstimadoUnit : null);
  const variacaoFromDet = detalhamento?.variacaoMinimaEntreLances;
  const tipoVarFromDet = detalhamento?.tipoVariacaoMinimaEntreLances;

  // Prioriza dados ao vivo (rankingData.itensGrupo) > último lance > proposta inicial > snapshot persistido (itemLimits)
  // Em fase Encerrada o itensGrupo NÃO tem melhorValorGeral/Fornecedor — caímos pros lances/propostas
  const ultimoLanceMeuValor = useMemo(() => {
    if (!Array.isArray(lancesItem) || lancesItem.length === 0) return null;
    // Pega o ÚLTIMO lance/proposta não excluído (ordenado por dataHoraInclusao)
    const sorted = [...lancesItem].filter((l) => !l.excluido).sort((a, b) =>
      String(b.dataHoraInclusao || '').localeCompare(String(a.dataHoraInclusao || ''))
    );
    return sorted[0]?.valor?.valorCalculado ?? sorted[0]?.valor?.valorInformado ?? null;
  }, [lancesItem]);

  // Validado via HAR (2026-05-19): /lances/por-participante/{N} retorna lances de TODOS
  // os fornecedores misturados (origens P=Proposta inicial, L=Lance, M=Melhor agregado),
  // SEM identificar de quem é cada um. Conclusões:
  //  - MELHOR (entre todos os fornecedores) = MIN(valor.valorCalculado) — confiável
  //  - MEU (só do IPCOM) = NÃO recuperável desse endpoint. Pra ter, precisa capturar
  //    /em-selecao-fornecedores/participacoes/{CNPJ_IPCOM}/itens/-1/itens-grupo/propostas
  //    (endpoint disparado quando user clica em "Proposta" do próprio IPCOM no portal).
  const melhorEntreTodos = useMemo(() => {
    if (!Array.isArray(lancesItem) || lancesItem.length === 0) return null;
    const valores = lancesItem
      .filter((l) => !l.excluido)
      .map((l) => l.valor?.valorCalculado ?? l.valor?.valorInformado)
      .filter((v) => typeof v === 'number') as number[];
    return valores.length > 0 ? Math.min(...valores) : null;
  }, [lancesItem]);

  // MELHOR (entre todos): fase ativa do itensGrupo OU min do histórico de lances
  const melhor: number | null = itensGrupoRow?.melhorValorGeral?.valorCalculado
    ?? itensGrupoRow?.melhorValorGeral?.valorInformado
    ?? melhorEntreTodos
    ?? (typeof item.bestBid === 'number' ? item.bestBid : null);
  // F-7 (Onda 4): PROCEDÊNCIA do número — a cascata mistura fontes com frescor
  // diferente e a tela mostrava tudo igual (na CRMV o operador não tinha como
  // saber se o "Melhor" era ao vivo ou snapshot velho).
  const melhorFonte = (itensGrupoRow?.melhorValorGeral?.valorCalculado ?? itensGrupoRow?.melhorValorGeral?.valorInformado) != null
    ? { label: '● ao vivo', cls: 'text-emerald-400', tip: 'Vindo do poll da disputa (em-disputa/itens-grupo) — atualiza a cada poucos segundos' }
    : melhorEntreTodos != null
      ? { label: '◐ histórico', cls: 'text-sky-400', tip: 'Menor valor do HISTÓRICO de lances coletado — pode defasar do portal' }
      : melhor != null
        ? { label: '○ snapshot', cls: 'text-slate-500', tip: 'Snapshot persistido da última coleta (bestBid) — dado da época da coleta, não ao vivo' }
        : null;

  // MEU (só do IPCOM): prioriza fase ativa (itensGrupo) > meuLanceUnitarioPorItem (endpoint
  // /em-selecao-fornecedores/participacoes/{IPCOM}/itens/-1/itens-grupo/propostas) > item.lastBid
  const meu: number | null = itensGrupoRow?.melhorValorFornecedor?.valorCalculado
    ?? itensGrupoRow?.melhorValorFornecedor?.valorInformado
    ?? (typeof meuValorUnitario === 'number' ? meuValorUnitario : null)
    ?? (typeof item.lastBid === 'number' ? item.lastBid : null);
  const meuFonte = (itensGrupoRow?.melhorValorFornecedor?.valorCalculado ?? itensGrupoRow?.melhorValorFornecedor?.valorInformado) != null
    ? { label: '● ao vivo', cls: 'text-emerald-400', tip: 'Vindo do poll da disputa — atualiza a cada poucos segundos' }
    : (typeof meuValorUnitario === 'number')
      ? { label: '◐ seleção', cls: 'text-sky-400', tip: 'Da Seleção de Fornecedores (pós-encerramento)' }
      : meu != null
        ? { label: '○ snapshot', cls: 'text-slate-500', tip: 'Snapshot persistido (lastBid) — dado da época da coleta' }
        : null;
  const situacaoP = itensGrupoRow?.situacaoParticipanteDisputa;
  const desclass = itensGrupoRow?.desclassificado === true;
  // Se fase = 'E' (Encerrada) sobrescreve detalheSituacaoDisputaItem (que pode estar com texto stale "prorrogação")
  const detalheSit = itensGrupoRow?.fase === 'E'
    ? 'Encerrado'
    : (itensGrupoRow?.detalheSituacaoDisputaItem || item.situation);
  const variacaoMin = itensGrupoRow?.variacaoMinimaEntreLances ?? variacaoFromDet;
  const tipoVar = itensGrupoRow?.tipoVariacaoMinimaEntreLances ?? tipoVarFromDet; // P=percentual

  // Status grande no centro: SÓ confia em `situacaoParticipanteDisputa` do itensGrupo
  // (vem do portal). Comparar melhor==meu localmente é enganoso pq o "melhor" pode
  // ser nosso próprio valor caso o endpoint não retorne dados de outros fornecedores.
  const statusInfo = useMemo(() => {
    if (desclass) return STATUS_PARTICIPANTE.D;
    if (situacaoP && STATUS_PARTICIPANTE[situacaoP]) return STATUS_PARTICIPANTE[situacaoP];
    return null;
  }, [situacaoP, desclass]);

  // Diff vs melhor (em % e absoluto)
  const diffPct = useMemo(() => {
    if (melhor == null || meu == null || melhor === 0) return null;
    return ((meu - melhor) / melhor) * 100;
  }, [melhor, meu]);

  const StatusIcon = statusInfo?.icon;

  // Countdown live por item — em PREGÃO POR ITEM cada item tem fimContagem
  // independente (encerra no seu tempo); o header só mostra o menor, aqui
  // exibimos o relógio do item em si. Fontes: itensGrupoRow (GRUPO, via
  // rankingData.itensGrupo) OU item.fimContagem (POR ITEM, via itemLimits do
  // em-disputa) — sem o fallback, pregão por item ficava sem relógio por item
  // (rankingData.itensGrupo só popula em grupo). Dispensa por item: janela fixa
  // → fimContagem igual nos itens (1 relógio efetivo).
  const fimContagemRaw = itensGrupoRow?.dataHoraFimContagem ?? (item as any)?.fimContagem ?? null;
  const fimContagemMs = fimContagemRaw ? new Date(fimContagemRaw).getTime() : null;
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!fimContagemMs || fimContagemMs <= Date.now()) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [fimContagemMs]);
  const tempoMs = fimContagemMs ? fimContagemMs - nowTick : null;
  const tempoStr = (() => {
    if (tempoMs == null || !isFinite(tempoMs) || tempoMs <= 0) return null;
    const total = Math.floor(tempoMs / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  })();
  // Cor: vermelho < 1min, âmbar < 5min, verde restante
  const tempoCor = tempoMs == null ? '' : tempoMs < 60_000 ? 'text-red-400 border-red-700/50 bg-red-500/10' : tempoMs < 300_000 ? 'text-amber-300 border-amber-700/50 bg-amber-500/10' : 'text-emerald-300 border-emerald-700/40 bg-emerald-500/10';

  return (
    <div className="bg-central-cartao border border-central-borda hover:border-central-borda rounded-md px-3 py-2 transition-colors">
      {/* Linha única: # | descrição | qty | estimado | limite | melhor | meu | badge | lances */}
      <div className="flex items-center gap-3 text-xs">
        {/* # número */}
        <div className="flex-shrink-0 w-7 h-7 rounded bg-slate-700/60 text-slate-300 font-bold text-xs flex items-center justify-center">
          {item.itemNumber}
        </div>
        {/* Descrição + qty */}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-100 truncate" title={descricaoFull}>{descricaoFull}</div>
          <div className="text-[10px] text-slate-500 truncate">
            {qtdReal} {unidadeReal}
            {valorEstimadoUnit ? <span className="ml-1.5">· est <span className="font-mono">{fmtBRL4(valorEstimadoUnit)}</span>/un</span> : null}
            {valorEstimadoTotal && qtdReal > 1 ? <span className="ml-1.5 text-slate-600">= <span className="font-mono">{fmtBRL4(valorEstimadoTotal)}</span> total</span> : null}
          </div>
        </div>
        {/* Limite (input compacto) */}
        <div className="flex-shrink-0 text-right">
          <div className="text-[9px] uppercase text-yellow-500/80 tracking-wide leading-none mb-0.5">Limite</div>
          {/* D2 (Heitor, 12/10/2026): a edição inline do piso saiu. Ela valia também com a sessão `aguardando`, sem
              confirm, com `catch {}` vazio, e não replicava na extensão (a aba do robô seguia com o piso antigo). O piso
              só muda pela Configuração da disputa, que confirma, mostra antes → depois e replica. */}
          <div className="text-xs font-mono text-yellow-400 w-24 text-right" title="O piso muda em Configurar a disputa">{fmtNum4(item.limitValue)}</div>
          {/* Indicador "pode cobrir?" — compara piso × melhor unit do mercado.
              PCP global: o "melhor" é o total do LOTE, piso é unitário — a
              cobertura se decide no header (Σ piso × qtd vs global), não aqui. */}
          {!valorGlobalLote && item.limitValue != null && melhor != null && Number(item.limitValue) > 0 && (
            <div
              className={`text-[9px] mt-0.5 leading-none ${
                Number(item.limitValue) <= melhor ? 'text-emerald-400' : 'text-rose-400'
              }`}
              title={
                Number(item.limitValue) <= melhor
                  ? `Seu piso (R$ ${Number(item.limitValue).toFixed(4)}) é ≤ melhor do mercado (R$ ${melhor.toFixed(4)}). Robô pode cobrir.`
                  : `Seu piso (R$ ${Number(item.limitValue).toFixed(4)}) está ACIMA do melhor do mercado (R$ ${melhor.toFixed(4)}). Robô não cobre — vai pular este item.`
              }
            >
              {Number(item.limitValue) <= melhor ? '✓ pode cobrir' : '⚠ piso acima'}
            </div>
          )}
        </div>
        {/* Melhor unit — F-7: procedência visível (● vivo / ◐ histórico / ○ snapshot).
            PCP global: o número é o valor GLOBAL do lote (sessão 126, 08/09: "MELHOR
            snapshot R$ 85.850" num item de R$ 20 parecia unitário) → rótulo "lote". */}
        <div className="flex-shrink-0 text-right w-28" title={
          valorGlobalLote
            ? 'Valor GLOBAL do lote — o PCP disputa o lote inteiro por um único valor; não é unitário deste item.'
            : melhor == null ? 'Sem dados unitários — em fase Encerrada o portal só retorna totais do grupo. Abra "Lances do grupo" no header.' : melhorFonte?.tip || ''
        }>
          <div className="text-[9px] uppercase text-blue-400/80 tracking-wide leading-none mb-0.5">
            Melhor{valorGlobalLote
              ? <span className="ml-1 normal-case text-slate-500">lote</span>
              : melhorFonte && <span className={`ml-1 normal-case ${melhorFonte.cls}`}>{melhorFonte.label}</span>}
          </div>
          <div className={`text-xs font-mono font-medium ${melhor == null ? 'text-slate-600' : 'text-blue-300'}`}>{melhor == null ? '—' : fmtBRL4(melhor)}</div>
        </div>
        {/* Meu unit + diff */}
        <div className="flex-shrink-0 text-right w-28" title={
          valorGlobalLote
            ? 'Meu lance GLOBAL no lote — não é unitário deste item.'
            : meu == null ? 'Sem dados unitários — em fase Encerrada o portal só retorna totais do grupo.' : meuFonte?.tip || ''
        }>
          <div className="text-[9px] uppercase text-slate-400 tracking-wide leading-none mb-0.5">
            Meu{valorGlobalLote
              ? <span className="ml-1 normal-case text-slate-500">lote</span>
              : meuFonte && <span className={`ml-1 normal-case ${meuFonte.cls}`}>{meuFonte.label}</span>}
          </div>
          <div className={`text-xs font-mono font-medium ${meu == null ? 'text-slate-600' : statusInfo === STATUS_PARTICIPANTE.G ? 'text-emerald-300' : 'text-slate-200'}`}>{meu == null ? '—' : fmtBRL4(meu)}</div>
          {diffPct !== null && Math.abs(diffPct) > 0.001 && (
            <div className={`text-[9px] leading-none ${diffPct > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
              {diffPct > 0 ? `+${diffPct.toFixed(1)}%` : `${diffPct.toFixed(1)}%`}
            </div>
          )}
        </div>
        {/* Countdown live por item — só quando fimContagem é futuro */}
        {tempoStr && (
          <div className={`flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono font-bold tabular-nums ${tempoCor}`} title="Tempo restante deste item (dataHoraFimContagem)">
            <Clock size={10} />
            {tempoStr}
          </div>
        )}
        {/* Status badge */}
        <div className="flex-shrink-0 w-32 flex justify-center">
          {statusInfo && StatusIcon && (
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${statusInfo.color}`}>
              <StatusIcon size={10} />
              {statusInfo.label}
            </div>
          )}
        </div>
        {/* Lances button */}
        <button
          onClick={() => onOpenDrawer(item.itemNumber, item.description)}
          className="flex-shrink-0 px-2 py-0.5 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-[11px] transition-colors flex items-center gap-1"
        >
          Lances <ChevronRight size={10} />
        </button>
      </div>
      {/* Linha 2 (opcional): situação detalhada + variação mínima — só renderiza se tiver algo */}
      {(detalheSit || variacaoMin != null) && (
        <div className="mt-1.5 ml-10 flex items-center gap-2 text-[10px] text-slate-500">
          {detalheSit && (
            <span className={`px-1.5 py-0.5 rounded ${
              /encerrad/i.test(detalheSit) ? 'bg-slate-700/40 text-slate-400' :
              /prorrog/i.test(detalheSit) ? 'bg-amber-500/15 text-amber-300' :
              'bg-emerald-500/15 text-emerald-300'
            }`}>{detalheSit}</span>
          )}
          {variacaoMin != null && (
            <span>variação <span className="text-slate-300 font-mono">{tipoVar === 'P' ? `${variacaoMin}%` : fmtBRL4(variacaoMin)}</span></span>
          )}
          {itensGrupoRow?.podeEnviarLances === false && <span className="text-red-400">⚠ Sem lances</span>}
        </div>
      )}
    </div>
  );
}
